// Chat Service for frontend-user-classic
import { authService } from './authService'
import { resolveUserChatTenantId } from '../utils/tenantUtils'
import { withCommunicationServiceBaseUrl, getCommunicationServiceBaseUrl } from '../lib/apiBaseUrl'

export interface MessageAttachment {
    type: string
    url: string
    filename?: string
    size?: number
    mimeType?: string
}

export interface MessageReplyTo {
    id?: string
    messageId?: string
    content?: string
    sender?: string
    senderName?: string
}

export interface Conversation {
    id?: string
    conversationId?: string
    userLineId?: string
    participants?: {
        user?: { lineUserId?: string; userId?: string; name?: string }
        admin?: { adminId?: string; name?: string }
    }
    status: 'active' | 'closed' | 'archived'
    lastMessage?: {
        content?: string
        timestamp?: string | Date
        senderType?: 'user' | 'admin'
    }
    unreadCount?: number | { user?: number; admin?: number }
    createdAt?: string | Date
    updatedAt?: string | Date
}

export interface Message {
    id: string
    conversationId: string
    content: string
    contentType?: 'text' | 'image' | 'file' | 'sticker'
    attachments?: MessageAttachment[]
    replyTo?: MessageReplyTo
    senderType: 'user' | 'admin' | 'system'
    timestamp: string | Date
    read?: boolean
}

class ChatService {
    private communicationServiceUrl: string
    private baseServiceUrl: string
    private lastMarkedReadAtByConversation: Map<string, number> = new Map()

    constructor() {
        this.communicationServiceUrl = withCommunicationServiceBaseUrl('/api/v1/chat')
        this.baseServiceUrl = getCommunicationServiceBaseUrl()
    }

    private resolveTenantId(): string | undefined {
        const user = authService.getCurrentUser()
        const tenantId = resolveUserChatTenantId(user)
        return tenantId || undefined
    }

    private getAuthHeaders(): HeadersInit {
        const token = authService.getToken()
        const tenantId = this.resolveTenantId()
        return {
            'Content-Type': 'application/json',
            ...(tenantId ? { 'X-Tenant-ID': tenantId } : {}),
            ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        }
    }

    private async makeRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
        const url = `${this.communicationServiceUrl}${endpoint}`
        const headers = { ...this.getAuthHeaders(), ...options.headers }
        const response = await fetch(url, { ...options, headers })
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}))
            throw new Error(errorData.message || errorData.error || `HTTP error! status: ${response.status}`)
        }
        return response.json()
    }

    resolveFileUrl(path: string): string {
        if (path.startsWith('http')) return path
        return `${this.baseServiceUrl}${path}`
    }

    private normalizeMessage(m: any, fallbackConversationId?: string): Message {
        const contentRaw = m.content
        let text = ''
        let contentType: Message['contentType'] = 'text'
        let attachments: MessageAttachment[] | undefined

        if (typeof contentRaw === 'object' && contentRaw) {
            text = contentRaw.text ?? ''
            const ct = contentRaw.type
            if (ct === 'image' || ct === 'file' || ct === 'sticker') contentType = ct
            if (Array.isArray(contentRaw.attachments) && contentRaw.attachments.length) {
                attachments = contentRaw.attachments
            }
        } else {
            text = String(contentRaw ?? '')
        }

        if (!attachments && Array.isArray(m.attachments) && m.attachments.length) {
            attachments = m.attachments
        }

        let replyTo: MessageReplyTo | undefined
        if (m.replyTo && typeof m.replyTo === 'object') {
            replyTo = {
                id: m.replyTo.id ?? m.replyTo.messageId,
                content: typeof m.replyTo.content === 'string' ? m.replyTo.content : m.replyTo.content?.text,
                senderName: m.replyTo.senderName ?? m.replyTo.sender,
            }
        }

        return {
            id: m.messageId ?? m.id ?? m._id,
            conversationId: m.conversationId ?? fallbackConversationId ?? '',
            content: text,
            contentType,
            attachments,
            replyTo,
            senderType: (m.sender?.type ?? m.senderType ?? 'user') as Message['senderType'],
            timestamp: m.createdAt ?? m.timestamp ?? new Date().toISOString(),
            read: m.read,
        }
    }

    async getOrCreateConversation(userInfo?: any): Promise<Conversation> {
        const user = authService.getCurrentUser()
        if (!user) throw new Error('User not authenticated')

        const authUser = user as typeof user & { _id?: string; userId?: string; name?: string; phone?: string; mobile?: string }
        const authUserId = authUser.id || authUser._id || authUser.userId
        const userId = authUserId || userInfo?.userId || userInfo?._id
        if (!userId) { throw new Error('User ID not found') }

        try {
            const response = await this.makeRequest<{ success: boolean; conversations: Conversation[] }>(
                `/users/${userId}/conversations?status=active&limit=1`
            )
            if (response.success && response.conversations?.length) return response.conversations[0]

            const userName = userInfo?.name || authUser.name || user.username
            const userPhone = userInfo?.mobile || authUser.phone || authUser.mobile || user.mobile
            const createResponse = await this.makeRequest<{ success: boolean; conversation: Conversation }>(
                '/conversations',
                {
                    method: 'POST',
                    body: JSON.stringify({
                        userLineId: userId,
                        userDetails: { name: userName, username: user.username, phone: userPhone, userId, tenantId: user.tenantId || userInfo?.tenantId },
                        source: 'web_chat',
                    }),
                }
            )
            if (createResponse.success && createResponse.conversation) return createResponse.conversation
            throw new Error('Failed to create conversation')
        } catch (error: any) {
            if (error.message?.includes('404') || error.message?.includes('not found')) {
                try {
                    const createResponse = await this.makeRequest<{ success: boolean; conversation: Conversation }>(
                        '/conversations',
                        {
                            method: 'POST',
                            body: JSON.stringify({
                                userLineId: userId,
                                userDetails: { name: (user as any).name || user.username, username: user.username, phone: (user as any).phone || (user as any).mobile || user.mobile },
                                source: 'web_chat',
                            }),
                        }
                    )
                    if (createResponse.success && createResponse.conversation) return createResponse.conversation
                } catch (createError) {
                    console.error('❌ [ChatService] Failed to create conversation:', createError)
                }
            }
            throw error
        }
    }

    async getMessages(conversationId: string, limit = 50, since?: string): Promise<Message[]> {
        try {
            const sinceQuery = since ? `&since=${encodeURIComponent(since)}` : ''
            const response = await this.makeRequest<{ success: boolean; messages: Message[] }>(
                `/conversations/${conversationId}/messages?limit=${limit}${sinceQuery}`
            )
            if (response.success && response.messages) {
                return response.messages
                    .map((m: any) => this.normalizeMessage(m, conversationId))
                    .sort((a: Message, b: Message) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
            }
            return []
        } catch (error) {
            console.error('❌ [ChatService] Failed to get messages:', error)
            return []
        }
    }

    async sendMessage(
        conversationId: string,
        content: string,
        options?: { contentType?: string; attachments?: MessageAttachment[]; replyTo?: { id?: string; content?: string; senderName?: string } },
    ): Promise<Message> {
        const user = authService.getCurrentUser()
        const senderId = (user as any)?.userId ?? (user as any)?._id ?? (user as any)?.id
        if (!senderId) throw new Error('User not authenticated')

        const body: any = {
            senderId: String(senderId),
            senderType: 'user',
            content: {
                text: content,
                type: options?.contentType || 'text',
                ...(options?.attachments?.length ? { attachments: options.attachments } : {}),
            },
        }
        if (options?.attachments?.length) body.attachments = options.attachments
        if (options?.replyTo) body.replyTo = options.replyTo

        const response = await this.makeRequest<{ success: boolean; message: Message }>(
            `/conversations/${conversationId}/messages`,
            { method: 'POST', body: JSON.stringify(body) },
        )
        if (response.success && response.message) {
            return this.normalizeMessage(response.message, conversationId)
        }
        throw new Error('Failed to send message')
    }

    async uploadFile(file: File): Promise<{ success: boolean; url: string; filename: string; size: number; type: string }> {
        const formData = new FormData()
        formData.append('file', file)
        const token = authService.getToken()
        const tenantId = this.resolveTenantId()

        const response = await fetch(`${this.communicationServiceUrl}/upload`, {
            method: 'POST',
            headers: {
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
                ...(tenantId ? { 'X-Tenant-ID': tenantId } : {}),
            },
            body: formData,
        })
        if (!response.ok) throw new Error(`Upload failed: ${response.statusText}`)
        return response.json()
    }

    async deleteMessage(conversationId: string, messageId: string): Promise<{ success: boolean }> {
        return this.makeRequest(`/conversations/${conversationId}/messages/${messageId}`, { method: 'DELETE' })
    }

    async markAsRead(conversationId: string, userId?: string): Promise<void> {
        const now = Date.now()
        const lastMarkedReadAt = this.lastMarkedReadAtByConversation.get(conversationId) || 0
        if (now - lastMarkedReadAt < 5000) return

        const user = authService.getCurrentUser()
        const readerId = userId || user?.id || (user as any)?._id || (user as any)?.userId
        if (!readerId) return
        try {
            this.lastMarkedReadAtByConversation.set(conversationId, now)
            await this.makeRequest(`/conversations/${conversationId}/read`, {
                method: 'PUT',
                body: JSON.stringify({ readerType: 'user', readerId }),
            })
        } catch (error) {
            this.lastMarkedReadAtByConversation.delete(conversationId)
            console.error('❌ [ChatService] Failed to mark as read:', error)
        }
    }
}

export const chatService = new ChatService()
